import React, {useState} from 'react';
import ItemTotalProfit from "./ItemTotalProfit";
import WaitingPaymentsBreakdown from "./WaitingPaymentsBreakdown";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import dollar from "../../../utils/images/svg/dollar.svg";

const TotalProfit = ({date, data}) => {
  const [showWaitingBreakdown, setShowWaitingBreakdown] = useState(true);
  
  const totalGave = parseFloat(data?.total_gave) || 0;
  const profit = parseFloat(data?.profit) || 0;
  const percentage = totalGave > 0 ? ((profit / totalGave) * 100).toFixed(1) : '0';

  const issued_loans_count = parseInt(data?.issued_loans_count) || 0;
  const avg_daily_sales = parseFloat(data?.avg_daily_sales) || 0;
  const issued_amount = parseFloat(data?.total_issued_amount) || 0;
  const expected_profit_from_issued = parseFloat(data?.expected_profit_from_issued) || 0;
  const expected_profit_percentage = parseFloat(data?.expected_profit_percentage) || 0;
  const expected_return = parseFloat(data?.expected_return) || 0;
  
  const paid = parseFloat(data?.paid) || 0;
  const wait = parseFloat(data?.wait) || 0;
  const overdue = parseFloat(data?.overdue) || 0;

  const fund_balance_end = parseFloat(data?.fund_balance_end) || 0;
  const fund_balance_start = parseFloat(data?.fund_balance_start) || 0;
  const net_cash_flow = paid - issued_amount;
  const growth_percentage = fund_balance_start > 0 ? (net_cash_flow / fund_balance_start) * 100 : 0;
  const reinvested_profit = parseFloat(data?.reinvested_profit) || 0;

  return (
    <>
      <Box sx={{display: 'flex', flexWrap: 'wrap'}} mt='10px' mb='10px'>
        {false && (
          <>
            <ItemTotalProfit
              label='TOTAL GAVE'
              value={totalGave}
              currency='USD'
              iconUrl={dollar}
            />
            <ItemTotalProfit
              label='PROFIT'
              value={profit}
              currency='USD'
              iconUrl={dollar}
              percentage={percentage}
            />
          </>
        )}
        
        <Box width='100%' my={2}>
          <Divider>
            <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600, letterSpacing: 0.5 }}>
              ISSUED LOANS IN PERIOD
            </Typography>
          </Divider>
        </Box>

        <ItemTotalProfit
          label='NUMBER OF ISSUED LOANS'
          value={issued_loans_count}
          suffix={avg_daily_sales > 0 ? `(Avg daily: $${avg_daily_sales.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})})` : ''}
        />

        <ItemTotalProfit
          label='ISSUED AMOUNT'
          value={issued_amount}
          currency='USD'
          iconUrl={dollar}
        />

        <ItemTotalProfit
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              EXPECTED PROFIT
              <Tooltip 
                title="Expected Profit = (Expected Return from Issued Loans - Issued Amount). Shows the potential profit if all payments from issued loans are completed."
                arrow
                placement="top"
              >
                <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
              </Tooltip>
            </Box>
          }
          value={expected_profit_from_issued}
          currency='USD'
          iconUrl={dollar}
          percentage={expected_profit_percentage.toFixed(2)}
        />

        <ItemTotalProfit
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              EXPECTED RETURN
              <Tooltip 
                title="The total amount expected to be received from all issued loans."
                arrow
                placement="top"
              >
                <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
              </Tooltip>
            </Box>
          }
          value={expected_return}
          currency='USD'
          iconUrl={dollar}
        />
        
        <Box width='100%' my={2}>
          <Divider>
            <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600, letterSpacing: 0.5 }}>
              PAYMENTS IN PERIOD
            </Typography>
          </Divider>
        </Box>
        
        <ItemTotalProfit
          label='NUMBER OF PAYMENTS'
          value={data?.count || '0'}
        />
        
        <ItemTotalProfit
          label='PAID'
          value={paid}
          currency='USD'
          iconUrl={dollar}
        />
        
        <ItemTotalProfit
          label='WAITING FOR PAYMENT'
          value={wait}
          currency='USD'
          iconUrl={dollar}
          onClick={() => setShowWaitingBreakdown(!showWaitingBreakdown)}
        />
        
        <ItemTotalProfit
          label='OVERDUE'
          value={overdue}
          currency='USD'
          iconUrl={dollar}
        />

        <Box width='100%' my={2}>
          <Divider>
            <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600, letterSpacing: 0.5 }}>
              BUSINESS RESULT
            </Typography>
          </Divider>
        </Box>

        <ItemTotalProfit
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              FUND BALANCE
              <Tooltip
                title="Fund Balance = Initial Fund ($237,000) + Total Paid − Total Loans Issued (cumulative up to end date)"
                arrow
                placement="top"
              >
                <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
              </Tooltip>
            </Box>
          }
          value={fund_balance_end}
          currency='USD'
          iconUrl={dollar}
        />

        <ItemTotalProfit
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              NET CASH FLOW
              <Tooltip
                title="Net Cash Flow = Total Paid − Total Loans Issued in the selected period. Positive value means more cash came in than went out."
                arrow
                placement="top"
              >
                <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
              </Tooltip>
            </Box>
          }
          value={net_cash_flow}
          currency='USD'
          iconUrl={dollar}
        />

        <ItemTotalProfit
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              GROWTH
              <Tooltip
                title="Growth = Net Cash Flow / Fund Balance at Start of Period × 100%. Shows the relative change of the fund balance over the selected period."
                arrow
                placement="top"
              >
                <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
              </Tooltip>
            </Box>
          }
          value={growth_percentage.toFixed(2)}
          currency='%'
        />

        <ItemTotalProfit
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              REINVESTED PROFIT
              <Tooltip
                title="Reinvested Profit = Active Loan Amount − Returned Principal − Initial Fund ($237,000)"
                arrow
                placement="top"
              >
                <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
              </Tooltip>
            </Box>
          }
          value={reinvested_profit}
          currency='USD'
          iconUrl={dollar}
        />
      </Box>
      
      {showWaitingBreakdown && (
        <WaitingPaymentsBreakdown 
          payments={data?.payments || []} 
          onClose={() => setShowWaitingBreakdown(false)}
        />
      )}
    </>
  );
};

export default TotalProfit;